/* eslint-disable unicorn/prevent-abbreviations, unicorn/no-null, promise/prefer-await-to-callbacks, @typescript-eslint/use-unknown-in-catch-callback-variable, @typescript-eslint/no-unnecessary-condition */

import type { FC } from 'react';
import { useRef, useState } from 'react';
import {
    ActionIcon,
    Box,
    Button,
    Group,
    Menu,
    Modal,
    ScrollArea,
    Text,
    TextInput,
    Tooltip
} from '@mantine/core';
import { FilePlus, FolderPlus } from 'lucide-react';
import { notifications } from '@mantine/notifications';
import type { ValidationError } from 'yup';
import {
    useCreateFileMutation,
    useDeleteFileMutation,
    useDeleteFolderMutation,
    useFileListQuery,
    useRenameFileMutation,
    useRenameFolderMutation
} from '../Files.queries';
import useDesignerFilesStore from '../DesignerFiles.store';
import {
    FileTreeDropState,
    FileTreeMoveStatus,
    FileTreeNodeKind,
    FileTreeViewState
} from './FileTree.enums';
import { filePathSchema, folderPathSchema } from './FileTree.schema';
import type {
    FileTreeActions,
    FileTreeDragSource,
    FileTreeProps
} from './FileTree.types';
import {
    FILE_TREE_FOLDER_MARKER,
    getFileTreeBasename,
    getFileTreeDropState,
    getFileTreeMoveStatus,
    getParentPath,
    hasFileTreeDragData,
    isFileTreePathTaken,
    joinFileTreePath,
    parseGCSKeysToTree
} from './FileTree.utils';
import { FileTreeRow } from './FileTreeRow';
import { FileTreeState } from './FileTreeStates';
import { openConfirmDeleteModal } from '@/utils/ui';
import { cn } from '@/utils';

const FileTree: FC<FileTreeProps> = ({
    versionId,
    versionLabel,
    isReadOnly,
    isLoadingSession = false
}) => {
    const [expandedFolders, setExpandedFolders] = useState<Set<string>>(
        new Set()
    );
    const [newFileModalOpen, setNewFileModalOpen] = useState(false);
    const [newFilePath, setNewFilePath] = useState('');
    const [pathError, setPathError] = useState<string | undefined>();
    const [newFolderModalOpen, setNewFolderModalOpen] = useState(false);
    const [newFolderPath, setNewFolderPath] = useState('');
    const [folderPathError, setFolderPathError] = useState<
        string | undefined
    >();
    const [renamingPath, setRenamingPath] = useState<string | null>(null);
    const [dragSource, setDragSource] = useState<FileTreeDragSource | null>(
        null
    );
    const [isRootDropHovered, setIsRootDropHovered] = useState(false);
    const rootDragCounterRef = useRef(0);
    const [emptySpaceMenuOpen, setEmptySpaceMenuOpen] = useState(false);
    const [emptySpaceAnchorPos, setEmptySpaceAnchorPos] = useState<{
        x: number;
        y: number;
    }>({ x: 0, y: 0 });

    const activeFilePath = useDesignerFilesStore(
        (state) => state.activeFilePath
    );
    const openFile = useDesignerFilesStore((state) => state.openFile);
    const setActiveFile = useDesignerFilesStore((state) => state.setActiveFile);

    const pendingDeletePaths = useDesignerFilesStore(
        (state) => state.pendingDeletePaths
    );

    const { data: items, isLoading: isLoadingFiles } =
        useFileListQuery(versionId);
    const createFileMutation = useCreateFileMutation();
    const deleteFileMutation = useDeleteFileMutation();
    const deleteFolderMutation = useDeleteFolderMutation();
    const renameFileMutation = useRenameFileMutation();
    const renameFolderMutation = useRenameFolderMutation();

    const pendingRenamePath =
        (renameFileMutation.isPending
            ? (renameFileMutation.variables?.newPath ?? null)
            : null) ??
        (renameFolderMutation.isPending
            ? (renameFolderMutation.variables?.newPath ?? null)
            : null);

    const pendingCreatePath = createFileMutation.isPending
        ? (createFileMutation.variables?.path ?? null)
        : null;

    const isLoading = isLoadingSession || isLoadingFiles;

    const tree = parseGCSKeysToTree((items ?? []).map((i) => i.name));

    const handleToggleFolder = (path: string) => {
        setExpandedFolders((prev) => {
            const next = new Set(prev);
            if (next.has(path)) {
                next.delete(path);
            } else {
                next.add(path);
            }
            return next;
        });
    };

    const handleFileClick = (path: string) => {
        if (activeFilePath === path) {
            return;
        }

        setActiveFile(path);
    };

    const handleDeleteFile = (path: string) => {
        const filename = path.split('/').pop() ?? path;
        openConfirmDeleteModal({
            title: 'Delete file',
            children: `Delete "${filename}"? This cannot be undone.`,
            confirmLabel: 'Delete',
            onConfirm: () => {
                deleteFileMutation.mutate({ path });
            }
        });
    };

    const handleDeleteFolder = (path: string) => {
        const folderName = path.split('/').pop() ?? path;
        const visibleCount = (items ?? []).filter(
            (i) =>
                i.name.startsWith(`${path}/`) &&
                !i.name.endsWith(`/${FILE_TREE_FOLDER_MARKER}`)
        ).length;
        let summary = 'This folder is empty.';
        if (visibleCount > 0) {
            const fileLabel = visibleCount === 1 ? 'file' : 'files';
            summary = `This folder contains ${visibleCount} ${fileLabel} that will also be deleted.`;
        }
        openConfirmDeleteModal({
            title: `Delete folder "${folderName}"`,
            children: `${summary} This cannot be undone.`,
            confirmLabel: 'Delete',
            onConfirm: () => {
                deleteFolderMutation.mutate({ path });
            }
        });
    };

    const handleNewFileIn = (parentPath: string) => {
        setNewFilePath(parentPath ? `${parentPath}/` : '');
        setPathError(undefined);
        setNewFileModalOpen(true);
    };

    const handleNewFolderIn = (parentPath: string) => {
        setNewFolderPath(parentPath ? `${parentPath}/` : '');
        setFolderPathError(undefined);
        setNewFolderModalOpen(true);
    };

    const handleEmptySpaceContextMenu = (
        e: React.MouseEvent<HTMLDivElement>
    ) => {
        e.preventDefault();

        setEmptySpaceAnchorPos({ x: e.clientX, y: e.clientY });
        setEmptySpaceMenuOpen(true);
    };

    const handleStartRename = (path: string) => {
        setRenamingPath(path);
    };

    const handleCancelRename = () => {
        setRenamingPath(null);
    };

    const handleSubmitRename = (
        oldPath: string,
        newName: string,
        kind: FileTreeNodeKind
    ) => {
        setRenamingPath(null);
        const parent = getParentPath(oldPath);
        const newPath = joinFileTreePath(parent, newName);
        if (newPath === oldPath) {
            return;
        }
        if (isFileTreePathTaken(items, newPath)) {
            notifications.show({
                color: 'red',
                message:
                    'An item with that name already exists at the destination.'
            });
            return;
        }
        if (kind === FileTreeNodeKind.File) {
            renameFileMutation.mutate({ oldPath, newPath });
        } else {
            setExpandedFolders((prev) => {
                const next = new Set(prev);
                if (prev.has(oldPath)) {
                    next.delete(oldPath);
                    next.add(newPath);
                }
                return next;
            });
            renameFolderMutation.mutate({ oldPath, newPath });
        }
    };

    const handleDragSourceStart = (path: string, kind: FileTreeNodeKind) => {
        setDragSource({ path, kind });
    };

    const handleDragSourceEnd = () => {
        setDragSource(null);
        rootDragCounterRef.current = 0;
        setIsRootDropHovered(false);
    };

    const handleMove = (targetFolderPath: string) => {
        if (!dragSource) {
            return;
        }
        const moveStatus = getFileTreeMoveStatus(dragSource, targetFolderPath);

        if (
            moveStatus === FileTreeMoveStatus.Self ||
            moveStatus === FileTreeMoveStatus.SameParent
        ) {
            return;
        }
        if (moveStatus === FileTreeMoveStatus.Descendant) {
            notifications.show({
                color: 'red',
                message: 'Cannot move a folder into itself.'
            });
            return;
        }
        const newPath = joinFileTreePath(
            targetFolderPath,
            getFileTreeBasename(dragSource.path)
        );

        if (isFileTreePathTaken(items, newPath)) {
            notifications.show({
                color: 'red',
                message:
                    'An item with that name already exists at the destination.'
            });
            return;
        }

        if (targetFolderPath) {
            setExpandedFolders((prev) => {
                if (prev.has(targetFolderPath)) {
                    return prev;
                }
                const next = new Set(prev);
                next.add(targetFolderPath);
                return next;
            });
        }
        if (dragSource.kind === FileTreeNodeKind.File) {
            renameFileMutation.mutate({ oldPath: dragSource.path, newPath });
        } else {
            renameFolderMutation.mutate({ oldPath: dragSource.path, newPath });
        }
    };

    const actions: FileTreeActions = {
        onFileClick: handleFileClick,
        onToggleFolder: handleToggleFolder,
        onDeleteFile: handleDeleteFile,
        onDeleteFolder: handleDeleteFolder,
        onStartRename: handleStartRename,
        onSubmitRename: handleSubmitRename,
        onCancelRename: handleCancelRename,
        onNewFileIn: handleNewFileIn,
        onNewFolderIn: handleNewFolderIn,
        onDragSourceStart: handleDragSourceStart,
        onDragSourceEnd: handleDragSourceEnd,
        onMove: handleMove
    };

    const rootMoveStatus = dragSource
        ? getFileTreeMoveStatus(dragSource, '')
        : FileTreeMoveStatus.Allowed;

    const rootDropState = getFileTreeDropState(
        isRootDropHovered,
        rootMoveStatus
    );

    const handleRootDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
        if (!hasFileTreeDragData(e.dataTransfer)) {
            return;
        }
        e.preventDefault();
        rootDragCounterRef.current += 1;
        if (rootDragCounterRef.current === 1) {
            setIsRootDropHovered(true);
        }
    };

    const handleRootDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        if (!hasFileTreeDragData(e.dataTransfer)) {
            return;
        }
        e.preventDefault();
        e.dataTransfer.dropEffect =
            rootMoveStatus === FileTreeMoveStatus.Allowed ? 'move' : 'none';
    };

    const handleRootDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
        if (!hasFileTreeDragData(e.dataTransfer)) {
            return;
        }
        rootDragCounterRef.current -= 1;
        if (rootDragCounterRef.current <= 0) {
            rootDragCounterRef.current = 0;
            setIsRootDropHovered(false);
        }
    };

    const handleRootDrop = (e: React.DragEvent<HTMLDivElement>) => {
        if (!hasFileTreeDragData(e.dataTransfer)) {
            return;
        }
        e.preventDefault();
        rootDragCounterRef.current = 0;
        setIsRootDropHovered(false);
        if (rootMoveStatus === FileTreeMoveStatus.Allowed) {
            handleMove('');
        }
    };

    const handleNewFilePathChange = (
        e: React.ChangeEvent<HTMLInputElement>
    ) => {
        setNewFilePath(e.target.value);
        if (pathError) {
            setPathError(undefined);
        }
    };

    const handleCreateFile = (): void => {
        filePathSchema
            .validate(newFilePath)
            .then(() => {
                const path = newFilePath;
                createFileMutation.mutate(
                    { path },
                    {
                        onSuccess: () => {
                            openFile(path, '');
                        }
                    }
                );
                setNewFileModalOpen(false);
                setNewFilePath('');
                setPathError(undefined);
            })
            .catch((error: ValidationError) => {
                setPathError(error.message);
            });
    };

    const handleDiscard = () => {
        setNewFileModalOpen(false);
        setNewFilePath('');
        setPathError(undefined);
    };

    const handleNewFolderPathChange = (
        e: React.ChangeEvent<HTMLInputElement>
    ) => {
        setNewFolderPath(e.target.value);
        if (folderPathError) {
            setFolderPathError(undefined);
        }
    };

    const handleCreateFolder = () => {
        folderPathSchema
            .validate(newFolderPath)
            .then(() => {
                const folderPath = newFolderPath.replace(/\/$/, '');
                const markerPath = `${folderPath}/${FILE_TREE_FOLDER_MARKER}`;
                createFileMutation.mutate({ path: markerPath });
                setExpandedFolders((prev) => {
                    const next = new Set(prev);
                    next.add(folderPath);
                    return next;
                });
                setNewFolderModalOpen(false);
                setNewFolderPath('');
                setFolderPathError(undefined);
            })
            .catch((error: ValidationError) => {
                setFolderPathError(error.message);
            });
    };

    const handleDiscardFolder = () => {
        setNewFolderModalOpen(false);
        setNewFolderPath('');
        setFolderPathError(undefined);
    };

    if (!versionId) {
        return <FileTreeState state={FileTreeViewState.NoVersion} />;
    }

    let treeContent = (
        <Box
            className={cn(
                'flex min-h-full flex-col',
                rootDropState === FileTreeDropState.Valid &&
                    'outline outline-2 -outline-offset-2 outline-blue-5',
                rootDropState === FileTreeDropState.Invalid &&
                    'outline outline-2 -outline-offset-2 outline-red-5'
            )}
            onContextMenu={handleEmptySpaceContextMenu}
            onDragEnter={handleRootDragEnter}
            onDragLeave={handleRootDragLeave}
            onDragOver={handleRootDragOver}
            onDrop={handleRootDrop}
        >
            {tree.map((node) => (
                <FileTreeRow
                    actions={actions}
                    activeFilePath={activeFilePath}
                    depth={0}
                    dragSource={dragSource}
                    expandedFolders={expandedFolders}
                    isReadOnly={isReadOnly}
                    key={`${node.kind}-${node.name}`}
                    node={node}
                    pendingCreatePath={pendingCreatePath}
                    pendingDeletePaths={pendingDeletePaths}
                    pendingRenamePath={pendingRenamePath}
                    renamingPath={renamingPath}
                />
            ))}
        </Box>
    );

    if (isLoading) {
        treeContent = <FileTreeState state={FileTreeViewState.Loading} />;
    } else if (tree.length === 0) {
        treeContent = (
            <Box
                className="flex min-h-full flex-col"
                onContextMenu={handleEmptySpaceContextMenu}
                onDragEnter={handleRootDragEnter}
                onDragLeave={handleRootDragLeave}
                onDragOver={handleRootDragOver}
                onDrop={handleRootDrop}
            >
                <FileTreeState state={FileTreeViewState.Empty} />
            </Box>
        );
    }

    return (
        <Box className="flex h-full flex-col bg-gray-1">
            <Box className="flex h-10 shrink-0 items-center justify-between gap-2 border-b border-gray-3 px-3">
                <Text truncate className="font-mono" fw={700} size="xs">
                    {versionLabel ?? ''}
                </Text>
                <Group className="shrink-0" gap={4} wrap="nowrap">
                    <Tooltip
                        label={
                            isReadOnly
                                ? 'Published versions are read-only'
                                : 'New file'
                        }
                        position="bottom"
                    >
                        <ActionIcon
                            aria-label="New file"
                            disabled={isReadOnly}
                            size="sm"
                            variant="subtle"
                            onClick={() => {
                                handleNewFileIn('');
                            }}
                        >
                            <FilePlus size={14} />
                        </ActionIcon>
                    </Tooltip>
                    <Tooltip
                        label={
                            isReadOnly
                                ? 'Published versions are read-only'
                                : 'New folder'
                        }
                        position="bottom"
                    >
                        <ActionIcon
                            aria-label="New folder"
                            disabled={isReadOnly}
                            size="sm"
                            variant="subtle"
                            onClick={() => {
                                handleNewFolderIn('');
                            }}
                        >
                            <FolderPlus size={14} />
                        </ActionIcon>
                    </Tooltip>
                </Group>
            </Box>

            <ScrollArea
                className="flex-1"
                onContextMenu={handleEmptySpaceContextMenu}
            >
                {treeContent}
            </ScrollArea>

            <Modal
                centered
                opened={newFileModalOpen}
                title="New File"
                onClose={handleDiscard}
            >
                <Box className="flex flex-col gap-4">
                    <TextInput
                        autoFocus
                        error={pathError}
                        label="File path"
                        placeholder="agents/tools/new_tool.py"
                        value={newFilePath}
                        onChange={handleNewFilePathChange}
                    />
                    <Box className="flex justify-end gap-2">
                        <Button variant="default" onClick={handleDiscard}>
                            Discard
                        </Button>
                        <Button
                            loading={createFileMutation.isPending}
                            onClick={handleCreateFile}
                        >
                            Create File
                        </Button>
                    </Box>
                </Box>
            </Modal>

            <Modal
                centered
                opened={newFolderModalOpen}
                title="New Folder"
                onClose={handleDiscardFolder}
            >
                <Box className="flex flex-col gap-4">
                    <TextInput
                        autoFocus
                        description="An empty folder is materialized via a hidden marker file."
                        error={folderPathError}
                        label="Folder path"
                        placeholder="agents/tools"
                        value={newFolderPath}
                        onChange={handleNewFolderPathChange}
                    />
                    <Box className="flex justify-end gap-2">
                        <Button variant="default" onClick={handleDiscardFolder}>
                            Discard
                        </Button>
                        <Button
                            loading={createFileMutation.isPending}
                            onClick={handleCreateFolder}
                        >
                            Create Folder
                        </Button>
                    </Box>
                </Box>
            </Modal>

            <Menu
                withinPortal
                opened={emptySpaceMenuOpen}
                position="bottom-start"
                shadow="md"
                onChange={setEmptySpaceMenuOpen}
            >
                <Menu.Target>
                    <Box
                        aria-hidden
                        style={{
                            position: 'fixed',
                            left: emptySpaceAnchorPos.x,
                            top: emptySpaceAnchorPos.y,
                            width: 0,
                            height: 0,
                            pointerEvents: 'none'
                        }}
                    />
                </Menu.Target>
                <Menu.Dropdown>
                    <Menu.Item
                        disabled={isReadOnly}
                        leftSection={<FilePlus size={14} />}
                        onClick={() => {
                            setEmptySpaceMenuOpen(false);
                            handleNewFileIn('');
                        }}
                    >
                        New File…
                    </Menu.Item>
                    <Menu.Item
                        disabled={isReadOnly}
                        leftSection={<FolderPlus size={14} />}
                        onClick={() => {
                            setEmptySpaceMenuOpen(false);
                            handleNewFolderIn('');
                        }}
                    >
                        New Folder…
                    </Menu.Item>
                </Menu.Dropdown>
            </Menu>
        </Box>
    );
};

export default FileTree;
